/**
 * 列表类 UI（下拉、提及建议等）的键盘导航。
 *
 * TODO - 后续可改进点
 * - 滚动应由组件自身处理（若已知 item 高度）
 * - 焦点应锁定在列表内
 * - onSelect 更适合做成回调而非传入函数
 */
import { computed } from 'vue';
import { useKeyboardEvents } from './useKeyboardEvents';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { SHORTCUT_ACTIONS } from 'dashboard/components/widgets/modal/constants';

/**
 * 包装导航动作：有可选项时执行并 preventDefault，且允许在输入框焦点下触发。
 * @param {Function} action
 * @param {import('vue').Ref<Array>} items
 */
const createAction = (action, items) => ({
  action: e => {
    if (!items.value?.length) return;
    action();
    e.preventDefault();
  },
  allowOnFocusedInput: true,
});

/**
 * 组装导航用键盘事件表。
 * 上下方向键走可自定义配置；Ctrl+P / Ctrl+N 为固定的 Emacs 风格备用键。
 * @param {Function} moveSelectionUp
 * @param {Function} moveSelectionDown
 * @param {Function} [onSelect]
 * @param {import('vue').Ref<Array>} items
 * @param {Function} getKeySet
 */
const createKeyboardEvents = (
  moveSelectionUp,
  moveSelectionDown,
  onSelect,
  items,
  getKeySet
) => {
  const upKey = getKeySet(SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_UP) || 'ArrowUp';
  const downKey =
    getKeySet(SHORTCUT_ACTIONS.NAVIGATE_DROPDOWN_DOWN) || 'ArrowDown';

  const events = {
    [upKey]: createAction(moveSelectionUp, items),
    'Control+KeyP': createAction(moveSelectionUp, items),
    [downKey]: createAction(moveSelectionDown, items),
    'Control+KeyN': createAction(moveSelectionDown, items),
  };

  if (typeof onSelect === 'function') {
    events.Enter = createAction(onSelect, items);
  }

  return events;
};

/**
 * 根据方向计算下一个选中下标（首尾循环）。
 * @param {number} currentIndex
 * @param {number} itemsLength
 * @param {'up'|'down'} direction
 * @returns {number}
 */
const updateSelectionIndex = (currentIndex, itemsLength, direction) => {
  if (direction === 'up') {
    return currentIndex === 0 ? itemsLength - 1 : currentIndex - 1;
  }
  return currentIndex === itemsLength - 1 ? 0 : currentIndex + 1;
};

/**
 * @param {Object} options
 * @param {import('vue').Ref<Array>} options.items
 * @param {Function} [options.onSelect]
 * @param {Function} options.adjustScroll
 * @param {import('vue').Ref<number>} options.selectedIndex
 */
export function useKeyboardNavigableList({
  items,
  onSelect,
  adjustScroll,
  selectedIndex,
}) {
  const { getKeySet, resolvedShortcuts } = useKeyboardShortcuts();

  /** 按方向移动选中项并调整滚动 */
  const moveSelection = direction => {
    selectedIndex.value = updateSelectionIndex(
      selectedIndex.value,
      items.value.length,
      direction
    );
    adjustScroll();
  };

  const moveSelectionUp = () => moveSelection('up');
  const moveSelectionDown = () => moveSelection('down');

  /** 响应快捷键覆盖变更，重新组装事件表 */
  const keyboardEvents = computed(() => {
    void resolvedShortcuts.value;
    return createKeyboardEvents(
      moveSelectionUp,
      moveSelectionDown,
      onSelect,
      items,
      getKeySet
    );
  });

  useKeyboardEvents(keyboardEvents);

  return {
    moveSelectionUp,
    moveSelectionDown,
  };
}
